/* eslint-disable */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import ajax from '../utils/ajaxHelper';

// Create UserContext
const UserContext = createContext();

// Custom hook to use UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// UserProvider component
const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to get user data from cookie
  const getUserFromCookie = useCallback(() => {
    try {
      const userCookie = Cookies.get('user');
      if (userCookie) {
        const key = 'iFrasCwdJh';
        const bytes = CryptoJS.AES.decrypt(userCookie, key);
        const loggedInUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        return loggedInUserData;
      }
      return null;
    } catch (error) {
      console.error('Error decrypting user data from cookie:', error);
      return null;
    }
  }, []);

  // Function to fetch fresh user data from API
  const fetchUserFromAPI = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await ajax.post('user/get-user-details', { userId });

      if (response?.status && response?.usersResult && response.usersResult !== 'No Data.') {
        return response.usersResult;
      } else {
        throw new Error('No user data found');
      }
    } catch (err) {
      console.error('Error fetching user from API:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to load user data (try cookie first, then API)
  const loadUser = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // First, try to get user from cookie
      const cookieUser = getUserFromCookie();

      if (cookieUser?.id) {
        // Set user from cookie immediately for fast UI
        setUserData(cookieUser);

        // Then fetch fresh data from API in background
        const apiUser = await fetchUserFromAPI(cookieUser.id);
        if (apiUser) {
          setUserData(apiUser);
        }
      } else {
        // No cookie user found
        setUserData(null);
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError(err.message);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  }, [getUserFromCookie, fetchUserFromAPI]);

  // Function to update user data
  const updateUserData = useCallback((newUserData) => {
    setUserData(newUserData);
    setError(null);
  }, []);

  // Function to refresh user data from API
  const refreshUser = useCallback(async () => {
    if (userData?.id) {
      const freshUser = await fetchUserFromAPI(userData.id);
      if (freshUser) {
        setUserData(freshUser);
      }
    }
  }, [userData?.id, fetchUserFromAPI]);

  // Function to clear user data (logout)
  const clearUser = useCallback(() => {
    setUserData(null);
    setError(null);
    setLoading(false);

    // Also clear cookies on logout
    Cookies.remove('user');
    Cookies.remove('token');
    Cookies.remove('authToken');
  }, []);

  // Load user on component mount
  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to get user's full name
  const getFullName = useCallback(() => {
    if (!userData) return '';
    return `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
  }, [userData]);

  // Helper function to get user's email
  const getEmail = useCallback(() => {
    return userData?.emailId || '';
  }, [userData]);

  // Context value
  const contextValue = {
    // Main user data object
    userData,

    // State
    loading,
    error,

    // Actions
    updateUserData,
    refreshUser,
    clearUser,
    loadUser,

    // Computed values
    isLoggedIn: !!userData,
    userId: userData?.id,
    userRole: userData?.userRoles,

    // Helper getters for common use cases
    name: getFullName(),
    email: getEmail(),
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    profilePic: userData?.profilePic || '',
  };

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserProvider;
